import React from 'react';
import { Lock, ArrowRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const PLAN_ORDER = ['free', 'payg', 'starter', 'professional', 'business'];

const PLAN_LABELS = {
  free: 'Free',
  payg: 'Pay-as-you-go',
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business'
};

const FEATURE_LABELS = {
  mtpe: 'Machine Translation Post-Editing (MTPE)',
  cat: 'CAT Tool',
  cat_ai: 'CAT Tool',
  tqa: 'Translation Quality Assessment',
  review: 'Review',
  glossary: 'Glossary',
  batch_processing: 'Batch Processing',
  team: 'Team Management',
};

export default function FeatureLockScreen({ feature, requiredPlan, currentPlan }) {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const featureLabel = FEATURE_LABELS[feature] || feature;
  const requiredLabel = PLAN_LABELS[requiredPlan] || requiredPlan;
  const currentLabel = PLAN_LABELS[currentPlan] || currentPlan;

  const currentIdx = PLAN_ORDER.indexOf(currentPlan);
  const requiredIdx = PLAN_ORDER.indexOf(requiredPlan);
  const plansToUpgrade = PLAN_ORDER.slice(currentIdx + 1, requiredIdx + 1);

  return (
    <div style={{
      minHeight: '80vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '480px', width: '100%', textAlign: 'center'
      }}>
        {/* Lock Icon */}
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: isDarkMode
            ? 'linear-gradient(135deg, #1e3a5f, #1e3a8a)'
            : 'linear-gradient(135deg, #f0f4ff, #e0e7ff)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
          border: `2px solid ${isDarkMode ? '#1e40af' : '#c7d2fe'}`
        }}>
          <Lock size={36} color="#2563eb" />
        </div>

        {/* Title */}
        <h2 style={{
          fontSize: '24px', fontWeight: '700',
          color: isDarkMode ? '#f1f5f9' : '#1d1d1f',
          marginBottom: '8px'
        }}>
          {featureLabel} is locked
        </h2>

        <p style={{
          color: isDarkMode ? '#94a3b8' : '#6e6e73',
          fontSize: '15px', marginBottom: '32px', lineHeight: '1.6'
        }}>
          You're on the <strong>{currentLabel}</strong> plan. Upgrade to{' '}
          <strong>{requiredLabel}</strong> or higher to access {featureLabel}.
        </p>

        {/* What you'll get */}
        <div style={{
          background: isDarkMode ? '#1e293b' : '#f5f5f7',
          borderRadius: '16px', padding: '20px',
          marginBottom: '28px', textAlign: 'left'
        }}>
          <p style={{
            fontSize: '13px',
            color: isDarkMode ? '#94a3b8' : '#6e6e73',
            marginBottom: '12px', fontWeight: '600',
            textTransform: 'uppercase', letterSpacing: '0.5px'
          }}>
            Unlock with {requiredLabel}
          </p>
          {feature === 'cat' || feature === 'cat_ai' ? (
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: isDarkMode ? '#e2e8f0' : '#3c3c3c', fontSize: '14px', lineHeight: '2' }}>
              <li>Upload DOCX, XLSX, PPTX files</li>
              <li>Segment-by-segment translation</li>
              <li>Live preview with formatting</li>
              <li>Export translated documents</li>
            </ul>
          ) : feature === 'mtpe' ? (
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: isDarkMode ? '#e2e8f0' : '#3c3c3c', fontSize: '14px', lineHeight: '2' }}>
              <li>Post-edit machine translations</li>
              <li>Quality scoring per segment</li>
              <li>Batch file processing</li>
            </ul>
          ) : feature === 'tqa' ? (
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: isDarkMode ? '#e2e8f0' : '#3c3c3c', fontSize: '14px', lineHeight: '2' }}>
              <li>Automated quality assessment</li>
              <li>Error detection & scoring</li>
              <li>Detailed QA reports</li>
            </ul>
          ) : feature === 'team' ? (
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: isDarkMode ? '#e2e8f0' : '#3c3c3c', fontSize: '14px', lineHeight: '2' }}>
              <li>Invite up to 3 team members</li>
              <li>Shared word quota (500K/month)</li>
              <li>Team usage tracking</li>
            </ul>
          ) : (
            <ul style={{ margin: 0, padding: '0 0 0 16px', color: isDarkMode ? '#e2e8f0' : '#3c3c3c', fontSize: '14px', lineHeight: '2' }}>
              <li>Access to {featureLabel}</li>
              <li>All {currentLabel} features</li>
            </ul>
          )}
        </div>

        {/* Upgrade Button */}
        <button
          onClick={() => navigate('/pricing')}
          style={{
            width: '100%', padding: '14px', background: '#2563eb',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '16px', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '8px', marginBottom: '12px'
          }}
        >
          <Zap size={18} />
          Upgrade to {requiredLabel}
          <ArrowRight size={18} />
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          style={{
            width: '100%', padding: '12px', background: 'transparent',
            color: isDarkMode ? '#94a3b8' : '#6e6e73',
            border: `1px solid ${isDarkMode ? '#1e293b' : '#e5e5ea'}`,
            borderRadius: '12px', fontSize: '15px', cursor: 'pointer'
          }}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  );
}
